import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Video, Clock, User, Shield, Heart, Smartphone } from 'lucide-react';
import heroImage from '../assets/images/telehealthhero.jpg';
import howitsworkImage from '../assets/images/howitswork.jpg';
import doctor1Image from '../assets/images/doctor1jpg.jpg';
import doctor2Image from '../assets/images/doctor2.jpg';
import doctor3Image from '../assets/images/doctor3.jpg';
import tabletImage from '../assets/images/tablet.jpg';
import womanImage from '../assets/images/woman.jpg';

// Import icon images
import videoIcon from '../assets/icons/Video Consultations.png';
import availabilityIcon from '../assets/icons/Availability.png';
import expertDoctorIcon from '../assets/icons/expertdoctor.png';
import secureIcon from '../assets/icons/Secure & Private.png';
import personalizedIcon from '../assets/icons/personalized.png';
import easyAccessIcon from '../assets/icons/easy-access.png';

// Import How It Works icons
import signupIcon from '../assets/icons/signup.png';
import appointmentIcon from '../assets/icons/appointment-book.png';
import videoCallIcon from '../assets/icons/video-call.png';

const Home = () => {
  const { isAuthenticated } = useAuth();
  
  // Hero carousel images
  const heroImages = [
    { src: heroImage, alt: 'Healthcare professional providing telehealth consultation' },
    { src: doctor1Image, alt: 'Expert doctor consultation' },
    { src: doctor2Image, alt: 'Medical professional' },
    { src: doctor3Image, alt: 'Healthcare provider' },
    { src: tabletImage, alt: 'Telehealth on tablet' },
    { src: womanImage, alt: 'Patient consultation' }
  ];
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Auto-transition: Images change every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [heroImages.length]);
  
  const goToSlide = (index) => {
    setCurrentImageIndex(index);
  };

  const features = [
    {
      icon: Video,
      iconImage: videoIcon,
      title: 'Video Consultations',
      description: 'Connect with healthcare professionals from anywhere with high-quality video calls'
    },
    {
      icon: Clock,
      iconImage: availabilityIcon,
      title: '24/7 Availability',
      description: 'Access healthcare services around the clock, whenever you need them'
    },
    {
      icon: User,
      iconImage: expertDoctorIcon,
      title: 'Expert Doctors',
      description: 'Connect with qualified and experienced healthcare professionals'
    },
    {
      icon: Shield,
      iconImage: secureIcon,
      title: 'Secure & Private',
      description: 'Your health information is protected with bank-level security'
    },
    {
      icon: Heart,
      iconImage: personalizedIcon,
      title: 'Personalized Care',
      description: 'Receive tailored healthcare recommendations and treatment plans'
    },
    {
      icon: Smartphone,
      iconImage: easyAccessIcon,
      title: 'Easy Access',
      description: 'Use our platform on any device - desktop, tablet, or mobile'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[700px] lg:min-h-[800px] overflow-hidden">
        {/* Background Images Carousel */}
        <div className="absolute inset-0 w-full h-full">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentImageIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img 
                src={image.src} 
                alt={image.alt} 
                className="w-full h-full object-cover"
              />
              {/* Overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20"></div>
            </div>
          ))}
        </div>
        
        {/* Content Overlay */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full min-h-[600px] md:min-h-[700px] lg:min-h-[800px] flex items-center">
          <div className="w-full">
            <div className="max-w-2xl">
              {/* Text Content */}
              <div className="text-center lg:text-left">
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-lg">
                  Healthcare at Your
                  <span className="text-primary-300"> Fingertips</span>
                </h1>
                <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl lg:max-w-none drop-shadow-md">
                  Connect with healthcare professionals instantly through secure video consultations. 
                  Get expert medical advice, schedule appointments, and manage your health from anywhere.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  {isAuthenticated ? (
                    <Link to="/dashboard" className="btn-primary text-lg px-8 py-3 bg-white text-primary-600 hover:bg-gray-100">
                      Go to Dashboard
                    </Link>
                  ) : (
                    <>
                      <Link to="/register" className="btn-primary text-lg px-8 py-3 bg-white text-primary-600 hover:bg-gray-100">
                        Get Started Free
                      </Link>
                      <Link to="/login" className="btn-outline text-lg px-8 py-3 border-2 border-white text-white hover:bg-white hover:text-primary-600">
                        Sign In
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Dots Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentImageIndex
                  ? 'w-8 bg-white'
                  : 'w-2 bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose TeleHealth?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience healthcare reimagined with our comprehensive telehealth platform
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const iconColors = [
                'from-blue-500 to-blue-600',
                'from-purple-500 to-purple-600',
                'from-green-500 to-green-600',
                'from-red-500 to-red-600',
                'from-pink-500 to-pink-600',
                'from-indigo-500 to-indigo-600'
              ];
              
              return (
                <div 
                  key={index} 
                  className="card text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
                >
                  <div className={`inline-flex items-center justify-center w-24 h-24 bg-white rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300 relative overflow-hidden shadow-md`}>
                    <div className={`absolute inset-0 bg-gradient-to-br ${iconColors[index]} opacity-0 group-hover:opacity-20 transition-opacity duration-300`}></div>
                    {feature.iconImage ? (
                      <img 
                        src={feature.iconImage} 
                        alt={feature.title}
                        className="h-16 w-16 object-contain relative z-10 transition-all duration-300"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%',
                          display: 'block'
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', feature.iconImage);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => console.log('Image loaded:', feature.title)}
                      />
                    ) : (
                      <feature.icon className={`h-10 w-10 text-gray-700 group-hover:text-white relative z-10 transition-colors duration-300`} strokeWidth={2} />
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)), url(${howitsworkImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Get started with telehealth in three simple steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="relative inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold z-20">
                  1
                </div>
                <img 
                  src={signupIcon} 
                  alt="Sign Up" 
                  className="h-16 w-16 object-contain p-1"
                  style={{ 
                    filter: 'brightness(0) invert(1)', 
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    display: 'block',
                    width: 'auto',
                    height: 'auto'
                  }}
                  onError={(e) => {
                    console.error('Signup icon failed to load');
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">Sign Up</h3>
              <p className="text-gray-600">
                Create your account and complete your health profile in minutes
              </p>
            </div>
            
            <div className="text-center group">
              <div className="relative inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl mb-6 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold z-20">
                  2
                </div>
                <img 
                  src={appointmentIcon} 
                  alt="Book Appointment" 
                  className="h-16 w-16 object-contain p-1"
                  style={{ 
                    filter: 'brightness(0) invert(1)', 
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    display: 'block',
                    width: 'auto',
                    height: 'auto'
                  }}
                  onError={(e) => {
                    console.error('Appointment icon failed to load');
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">Book Appointment</h3>
              <p className="text-gray-600">
                Choose your preferred doctor and schedule a consultation time
              </p>
            </div>
            
            <div className="text-center group">
              <div className="relative inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl mb-6 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold z-20">
                  3
                </div>
                <img 
                  src={videoCallIcon} 
                  alt="Video Call" 
                  className="h-16 w-16 object-contain p-1"
                  style={{ 
                    filter: 'brightness(0) invert(1)', 
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    display: 'block',
                    width: 'auto',
                    height: 'auto'
                  }}
                  onError={(e) => {
                    console.error('Video call icon failed to load');
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">Video Call</h3>
              <p className="text-gray-600">
                Join your scheduled video consultation and get expert medical advice
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join thousands of patients who are already using TeleHealth for their healthcare needs
          </p>
          {!isAuthenticated && (
            <Link to="/register" className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg text-lg transition-colors duration-200">
              Start Your Free Trial
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
